import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuildConfig, useGuildPatch } from '../../../lib/auditQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';
import {
  useAutomodRules,
  useCreateAutomodRule,
  useDeleteAutomodRule,
  useRoles,
  useTestAutomodRule,
  useUpdateAutomodRule,
  type AutomodAction,
  type AutomodMatchType,
  type AutomodRule,
  type AutomodRuleInput,
} from '../../../lib/automodQueries.js';
import { ApiError } from '../../../lib/api.js';

export const Route = createFileRoute('/guilds/$guildId/automod')({
  component: AutomodPage,
});

const MATCH_TYPES: AutomodMatchType[] = [
  'keyword',
  'regex',
  'link_blacklist',
  'link_whitelist_only',
  'invite_block',
  'mass_mention',
];
const ACTIONS: AutomodAction[] = ['delete', 'warn', 'timeout', 'log_only'];

const PATTERN_REQUIRED: ReadonlySet<AutomodMatchType> = new Set([
  'keyword',
  'regex',
  'link_blacklist',
  'link_whitelist_only',
]);

function emptyDraft(): AutomodRuleInput {
  return {
    name: '',
    enabled: true,
    matchType: 'keyword',
    pattern: '',
    caseSensitive: false,
    threshold: null,
    action: 'delete',
    timeoutSeconds: null,
    warnReason: null,
    exemptRoleIds: [],
    exemptChannelIds: [],
  };
}

function fromRule(rule: AutomodRule): AutomodRuleInput {
  return {
    name: rule.name,
    enabled: rule.enabled,
    matchType: rule.matchType,
    pattern: rule.pattern,
    caseSensitive: rule.caseSensitive,
    threshold: rule.threshold,
    action: rule.action,
    timeoutSeconds: rule.timeoutSeconds,
    warnReason: rule.warnReason,
    exemptRoleIds: rule.exemptRoleIds,
    exemptChannelIds: rule.exemptChannelIds,
  };
}

function AutomodPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const rules = useAutomodRules(guildId);
  const config = useGuildConfig(guildId);
  const guildPatch = useGuildPatch(guildId);

  const [editing, setEditing] = useState<
    { kind: 'create' } | { kind: 'edit'; rule: AutomodRule } | null
  >(null);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <section className="mb-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
              checked={config.data?.automodEnabled ?? false}
              onChange={(e) => {
                guildPatch.mutate({ automodEnabled: e.target.checked });
              }}
            />
            <span>{t('automod.toggle')}</span>
          </label>
          <p className="mt-1 text-xs text-zinc-500">{t('automod.toggleHint')}</p>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-zinc-900/40">
          <header className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">
              {t('automod.rules.title')}
            </h2>
            <button
              type="button"
              onClick={() => {
                setEditing({ kind: 'create' });
              }}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
            >
              {t('automod.button.create')}
            </button>
          </header>

          {rules.isLoading ? (
            <p className="px-5 py-6 text-sm text-zinc-500">{t('common.loading')}</p>
          ) : !rules.data || rules.data.length === 0 ? (
            <p className="px-5 py-6 text-sm text-zinc-500">{t('automod.empty')}</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {rules.data.map((r) => (
                <RuleRow
                  key={r.id}
                  rule={r}
                  guildId={guildId}
                  onEdit={() => {
                    setEditing({ kind: 'edit', rule: r });
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </main>

      {editing ? (
        <RuleEditor
          guildId={guildId}
          mode={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const guildConfig = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            {
              label: guildConfig.data?.name ?? guildId,
              to: '/guilds/$guildId',
              params: { guildId },
            },
            { label: t('automod.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function RuleRow({
  rule,
  guildId,
  onEdit,
}: {
  rule: AutomodRule;
  guildId: string;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateAutomodRule(guildId);
  const remove = useDeleteAutomodRule(guildId);

  return (
    <li className="flex items-center gap-4 px-5 py-3">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        checked={rule.enabled}
        onChange={(e) => {
          update.mutate({ id: rule.id, patch: { enabled: e.target.checked } });
        }}
        title={t('automod.rules.enable')}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{rule.name}</p>
        <p className="text-xs text-zinc-500">
          {t(`automod.matchType.${rule.matchType}`)} · {t(`automod.action.${rule.action}`)}
          {rule.pattern
            ? ` · ${rule.pattern.length > 60 ? rule.pattern.slice(0, 60) + '…' : rule.pattern}`
            : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
      >
        {t('automod.button.edit')}
      </button>
      <button
        type="button"
        onClick={() => {
          if (window.confirm(t('automod.deleteConfirm', { name: rule.name }))) {
            remove.mutate(rule.id);
          }
        }}
        className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-rose-300 hover:bg-zinc-800"
      >
        {t('common.remove')}
      </button>
    </li>
  );
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  matchType?: string;
  issues?: { fieldErrors?: Record<string, string[]> };
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as ApiErrorBody | null;
    if (body?.message) return body.message;
    if (body?.error === 'PATTERN_REQUIRED') return `pattern required for ${body.matchType ?? ''}`;
    if (body?.error === 'UNSAFE_REGEX') return body.message ?? 'unsafe regex';
    if (body?.issues?.fieldErrors) {
      const parts: string[] = [];
      for (const [k, v] of Object.entries(body.issues.fieldErrors)) {
        if (v && v.length > 0) parts.push(`${k}: ${v.join(', ')}`);
      }
      if (parts.length > 0) return parts.join(' · ');
    }
    if (body?.error) return body.error;
  }
  return err instanceof Error ? err.message : fallback;
}

function RuleEditor({
  guildId,
  mode,
  onClose,
}: {
  guildId: string;
  mode: { kind: 'create' } | { kind: 'edit'; rule: AutomodRule };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateAutomodRule(guildId);
  const update = useUpdateAutomodRule(guildId);
  const roles = useRoles(guildId);

  const ruleId = mode.kind === 'edit' ? mode.rule.id : null;
  const test = useTestAutomodRule(guildId, ruleId ?? '');

  const [draft, setDraft] = useState<AutomodRuleInput>(
    mode.kind === 'edit' ? fromRule(mode.rule) : emptyDraft(),
  );
  const [sample, setSample] = useState('');
  const [sampleMentions, setSampleMentions] = useState('0');

  const roleOptions = useMemo(() => roles.data ?? [], [roles.data]);

  const isPending = create.isPending || update.isPending;

  const showPattern = PATTERN_REQUIRED.has(draft.matchType);
  const showCaseSensitive = draft.matchType === 'keyword' || draft.matchType === 'regex';
  const showThreshold = draft.matchType === 'mass_mention';
  const showTimeout = draft.action === 'timeout';

  const onSubmit = (): void => {
    if (mode.kind === 'create') {
      create.mutate(draft, { onSuccess: onClose });
    } else {
      update.mutate({ id: mode.rule.id, patch: draft }, { onSuccess: onClose });
    }
  };

  const submitError = create.error ?? update.error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <header className="sticky top-0 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-5 py-3">
          <h2 className="text-sm font-semibold">
            {mode.kind === 'create'
              ? t('automod.editor.titleCreate')
              : t('automod.editor.titleEdit')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.dismiss')}
          </button>
        </header>

        <div className="space-y-5 px-5 py-5">
          <Field label={t('automod.field.name')}>
            <input
              type="text"
              maxLength={100}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
              value={draft.name}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => ({ ...d, name: v }));
              }}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
              checked={draft.enabled}
              onChange={(e) => {
                const v = e.target.checked;
                setDraft((d) => ({ ...d, enabled: v }));
              }}
            />
            {t('automod.field.enabled')}
          </label>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('automod.field.matchType')}>
              <select
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={draft.matchType}
                onChange={(e) => {
                  const v = e.target.value as AutomodMatchType;
                  setDraft((d) => ({
                    ...d,
                    matchType: v,
                    pattern: PATTERN_REQUIRED.has(v) ? (d.pattern ?? '') : null,
                    threshold: v === 'mass_mention' ? (d.threshold ?? 5) : null,
                  }));
                }}
              >
                {MATCH_TYPES.map((m) => (
                  <option key={m} value={m}>
                    {t(`automod.matchType.${m}`)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('automod.field.action')}>
              <select
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={draft.action}
                onChange={(e) => {
                  const v = e.target.value as AutomodAction;
                  setDraft((d) => ({
                    ...d,
                    action: v,
                    timeoutSeconds: v === 'timeout' ? (d.timeoutSeconds ?? 600) : null,
                  }));
                }}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {t(`automod.action.${a}`)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {showPattern ? (
            <Field
              label={t('automod.field.pattern')}
              hint={t(`automod.patternHint.${draft.matchType}`)}
            >
              <textarea
                className="min-h-20 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-sm"
                value={draft.pattern ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft((d) => ({ ...d, pattern: v }));
                }}
                maxLength={500}
              />
            </Field>
          ) : null}

          {showCaseSensitive ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                checked={draft.caseSensitive}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDraft((d) => ({ ...d, caseSensitive: v }));
                }}
              />
              {t('automod.field.caseSensitive')}
            </label>
          ) : null}

          {showThreshold ? (
            <Field label={t('automod.field.threshold')}>
              <input
                type="number"
                min={2}
                max={50}
                className="w-32 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
                value={draft.threshold ?? 5}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setDraft((d) => ({ ...d, threshold: Number.isFinite(n) ? n : null }));
                }}
              />
            </Field>
          ) : null}

          {showTimeout ? (
            <Field label={t('automod.field.timeoutSeconds')} hint={t('automod.field.timeoutHint')}>
              <input
                type="number"
                min={1}
                max={2_419_200}
                className="w-32 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
                value={draft.timeoutSeconds ?? 600}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setDraft((d) => ({ ...d, timeoutSeconds: Number.isFinite(n) ? n : null }));
                }}
              />
            </Field>
          ) : null}

          <Field label={t('automod.field.warnReason')} hint={t('automod.field.warnReasonHint')}>
            <input
              type="text"
              maxLength={512}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
              value={draft.warnReason ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => ({ ...d, warnReason: v || null }));
              }}
            />
          </Field>

          <Field label={t('automod.field.exemptRoles')}>
            <select
              multiple
              className="h-32 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
              value={draft.exemptRoleIds}
              onChange={(e) => {
                const v = Array.from(e.target.selectedOptions, (o) => o.value);
                setDraft((d) => ({ ...d, exemptRoleIds: v }));
              }}
            >
              {roleOptions.map((r) => (
                <option key={r.id} value={r.id} style={{ color: r.color }}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('automod.field.exemptChannels')}>
            <ChannelPicker
              guildId={guildId}
              mode="multi"
              value={draft.exemptChannelIds}
              onChange={(v) => {
                setDraft((d) => ({ ...d, exemptChannelIds: v }));
              }}
            />
          </Field>

          {submitError ? (
            <p className="text-xs text-rose-400">
              {extractErrorMessage(submitError, t('common.error'))}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
            >
              {t('automod.button.cancel')}
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={isPending}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {isPending ? t('common.loading') : t('automod.button.save')}
            </button>
          </div>

          {ruleId ? (
            <section className="rounded-md border border-zinc-800 bg-zinc-900/50 p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                {t('automod.test.title')}
              </h3>
              <p className="mb-3 text-xs text-zinc-500">{t('automod.test.hint')}</p>
              <textarea
                className="mb-2 min-h-16 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-sm"
                placeholder={t('automod.test.samplePlaceholder')}
                value={sample}
                onChange={(e) => {
                  setSample(e.target.value);
                }}
                maxLength={4000}
              />
              {showThreshold ? (
                <Field label={t('automod.test.mentionCount')}>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    className="w-24 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
                    value={sampleMentions}
                    onChange={(e) => {
                      setSampleMentions(e.target.value);
                    }}
                  />
                </Field>
              ) : null}
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    test.mutate({
                      sample,
                      mentionCount: showThreshold ? Number(sampleMentions) || 0 : 0,
                    });
                  }}
                  disabled={test.isPending || sample.length === 0}
                  className="rounded-md border border-indigo-600 bg-indigo-600/10 px-3 py-1.5 text-sm text-indigo-300 hover:bg-indigo-600/20 disabled:opacity-50"
                >
                  {test.isPending ? t('common.loading') : t('automod.test.run')}
                </button>
                {test.data ? (
                  <span
                    className={`text-sm ${test.data.matched ? 'text-emerald-400' : 'text-zinc-400'}`}
                  >
                    {test.data.matched
                      ? t('automod.test.matched', {
                          action: t(`automod.action.${test.data.action ?? 'log_only'}`),
                        })
                      : t('automod.test.notMatched')}
                    {test.data.matchedSubstring ? ` — "${test.data.matchedSubstring}"` : ''}
                  </span>
                ) : null}
                {test.isError ? (
                  <span className="text-sm text-rose-400">
                    {extractErrorMessage(test.error, t('common.error'))}
                  </span>
                ) : null}
              </div>
            </section>
          ) : (
            <p className="text-xs text-zinc-500">{t('automod.test.saveFirst')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}
